import { ActionIcon, Badge, Box, Button, Group, Menu, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconPlus, IconX } from "@tabler/icons-react";
import type { IConnectedBimSpace } from "@schemas/issueSchema";
import React, { useState } from "react";
import { usePutProjectByProjectIdIssueConnectedBimSpacesMutation } from "../../../../../../openapi";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";
import { selectIssueBoard } from "../../issueSelectors";
import { thunkOpenIssueModalById } from "../../issueSlice";
import FloorplanConnectIssueModal from "./FloorplanConnectIssueModal";
import FloorplanCreateIssueModal from "./FloorplanCreateIssueModal";
import {
  selectConnectedBimSpaceForSelectedFloorSpace,
  selectHiddenIssueCountForSelectedFloorSpace,
  selectIssueIdsForSelectedFloorSpace,
  selectIssuesForSelectedFloorSpace,
  selectSelectedFloorSpace,
} from "./floorplanSelectors";
import { setSelectedFloorSpaceIdInRedux } from "./floorplanSlice";

const FloorplanSelectedSpacePanel: React.FC = () => {
  const [createIssueModalOpen, setCreateIssueModalOpen] = useState(false);
  const [connectIssueModalOpen, setConnectIssueModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.adminReducer.project);
  const issueBoard = useAppSelector(selectIssueBoard);
  const issueBoardId = useAppSelector(selectIssueBoardId);
  const selectedSpace = useAppSelector(selectSelectedFloorSpace);
  const connectedBimSpace = useAppSelector(selectConnectedBimSpaceForSelectedFloorSpace);
  const issues = useAppSelector(selectIssuesForSelectedFloorSpace);
  const issueIdsForSelectedFloorSpace = useAppSelector(selectIssueIdsForSelectedFloorSpace);
  const hiddenIssueCount = useAppSelector(selectHiddenIssueCountForSelectedFloorSpace);
  const [updateConnectedBimSpaces] = usePutProjectByProjectIdIssueConnectedBimSpacesMutation();

  if (selectedSpace == null) {
    return null;
  }

  const canConnectIssue = project != null && issueBoard != null && connectedBimSpace != null;

  const isSameRoomConnection = (space: IConnectedBimSpace) =>
    (space.floor ?? "").trim() === (connectedBimSpace?.floor ?? "").trim() &&
    (space.roomNumber ?? "").trim() === (connectedBimSpace?.roomNumber ?? "").trim();

  const getConnectedBimSpacesPayload = (spaces: IConnectedBimSpace[]) =>
    spaces.map((space) => ({
      ...space,
      roomNumber: space.roomNumber ?? "",
    }));

  const handleDisconnectIssueFromRoom = async (issue: (typeof issues)[number]) => {
    if (project == null || connectedBimSpace == null) {
      return;
    }

    await updateConnectedBimSpaces({
      projectId: project._id,
      body: {
        issueId: issue._id,
        connectedBimSpaces: getConnectedBimSpacesPayload(
          (issue.connectedBimSpaces ?? []).filter((space) => !isSameRoomConnection(space)),
        ),
      },
    }).unwrap();
  };

  return (
    <>
      <aside
        style={{
          width: 292,
          flexShrink: 0,
          alignSelf: "stretch",
          borderLeft: "1px solid var(--mantine-color-gray-3)",
          overflow: "hidden",
          background: "var(--mantine-color-gray-0)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Group
          justify="space-between"
          align="flex-start"
          wrap="nowrap"
          style={{
            padding: "0.75rem 1rem",
            background: "var(--mantine-color-gray-1)",
            borderBottom: "1px solid var(--mantine-color-gray-3)",
          }}
        >
          <Box>
            <Text fw={700}>{selectedSpace.name || "Uten romnummer"}</Text>

            {selectedSpace.longName && (
              <Text size="sm" c="dimmed" mt={2}>
                {selectedSpace.longName}
              </Text>
            )}
          </Box>

          <Tooltip label="Fjern markering" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => {
                if (issueBoardId == null) return;

                dispatch(setSelectedFloorSpaceIdInRedux({ issueBoardId, selectedFloorSpaceId: "" }));
              }}
            >
              <IconX size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Stack gap="md" style={{ padding: "1rem", minHeight: 0, flex: 1 }}>
          <Menu shadow="md" width={190} disabled={!canConnectIssue}>
            <Menu.Target>
              <Button
                fullWidth
                color="mc-primary"
                variant="light"
                leftSection={<IconPlus size={16} />}
                rightSection={<IconChevronDown size={14} />}
                disabled={!canConnectIssue}
              >
                Koble sak
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => setCreateIssueModalOpen(true)}>Ny sak</Menu.Item>
              <Menu.Item onClick={() => setConnectIssueModalOpen(true)}>Eksisterende sak</Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Stack
            gap="xs"
            style={{
              background: "white",
              border: "1px solid var(--mantine-color-gray-2)",
              borderRadius: "6px",
              padding: "0.875rem",
              minHeight: 0,
              flex: 1,
              overflow: "hidden",
            }}
          >
            <Group justify="space-between" align="center" wrap="nowrap">
              <Text size="sm" fw={600}>
                Saker i rommet
              </Text>

              {hiddenIssueCount > 0 && (
                <Tooltip label="Disse sakene er koblet til rommet, men skjules av aktive filtre" withArrow>
                  <Badge color="yellow" variant="light">
                    {hiddenIssueCount} skjult
                  </Badge>
                </Tooltip>
              )}
            </Group>

            {issues.length === 0 && (
              <Text size="sm" c="dimmed" lh={1.45}>
                Ingen saker koblet til dette rommet.
              </Text>
            )}

            <Box style={{ minHeight: 0, overflowY: "auto", flex: 1 }}>
              <Stack gap="xs">
                {issues.map((issue) => (
                  <UnstyledButton
                    key={issue._id}
                    onClick={() => dispatch(thunkOpenIssueModalById(issue._id))}
                    style={{
                      display: "block",
                      width: "100%",
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: "6px",
                      padding: "0.75rem",
                      textAlign: "left",
                      background: "var(--mantine-color-gray-0)",
                    }}
                  >
                    <Stack gap={6}>
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Text size="sm" fw={700}>
                          {issue.id || issue._id}
                        </Text>

                        <Group gap={6} wrap="nowrap" align="center">
                          <Box className="statusdot" style={{ backgroundColor: issue.statusColor }} />
                          <Text size="xs" c="dimmed">
                            {issue.status}
                          </Text>
                          <Tooltip label="Fjern kobling til rom" withArrow>
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="gray"
                              onClick={(event) => {
                                event.stopPropagation();
                                void handleDisconnectIssueFromRoom(issue);
                              }}
                            >
                              <IconX size={12} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Group>

                      <Text size="sm" lineClamp={2}>
                        {issue.title || "Uten tittel"}
                      </Text>
                    </Stack>
                  </UnstyledButton>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </aside>

      {project != null && connectedBimSpace != null && (
        <>
          {issueBoard?._id != null && (
            <FloorplanCreateIssueModal
              opened={createIssueModalOpen}
              projectId={project._id}
              issueBoardId={issueBoard._id}
              connectedBimSpaces={[connectedBimSpace]}
              onClose={() => setCreateIssueModalOpen(false)}
            />
          )}
          <FloorplanConnectIssueModal
            opened={connectIssueModalOpen}
            projectId={project._id}
            issueBoardId={issueBoard?._id}
            connectedBimSpaces={[connectedBimSpace]}
            blockedIssueIds={issueIdsForSelectedFloorSpace}
            onClose={() => setConnectIssueModalOpen(false)}
          />
        </>
      )}
    </>
  );
};

export default FloorplanSelectedSpacePanel;
